/**
 * Assigned Projects Component for Developers
 * Shows developer's assigned projects with GitHub integration
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Stack,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  BugReport as IssueIcon,
  MergeType as PRIcon,
  Code as CodeIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';

export interface AssignedProject {
  id: number;
  name: string;
  client: string;
  status: string;
  progress: number;
  technologies: string[];
  tasksTotal: number;
  tasksCompleted: number;
  projectManager?: string;
  startDate?: string;
  endDate?: string;
  githubRepo?: string;
  githubBranch?: string;
  openPRs?: number;
  openIssues?: number;
  lastCommit?: string;
  description?: string;
}

interface AssignedProjectsProps {
  projects: AssignedProject[];
  onViewSpec?: (projectId: number) => void;
  onOpenGitHub?: (repoUrl: string) => void;
}

const AssignedProjects: React.FC<AssignedProjectsProps> = ({
  projects,
  onViewSpec,
  onOpenGitHub,
}) => {
  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'in progress':
        return 'primary';
      case 'review':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleGitHubClick = (project: AssignedProject) => {
    if (project.githubRepo) {
      const url = project.githubRepo.startsWith('http')
        ? project.githubRepo
        : `https://github.com/${project.githubRepo}`;
      
      if (onOpenGitHub) {
        onOpenGitHub(url);
      } else {
        window.open(url, '_blank');
      }
    }
  };

  if (projects.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box textAlign="center" py={4}>
            <CodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Projects Assigned
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll see your assigned projects here once a project manager assigns you to a project.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {projects.map((project) => (
        <Card key={project.id} sx={{ position: 'relative' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  {project.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Client: {project.client}
                  </Typography>
                  {project.projectManager && (
                    <>
                      <Typography color="text.secondary">•</Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          PM: {project.projectManager}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              </Box>
              <Chip
                label={formatStatus(project.status)}
                color={getStatusColor(project.status)}
                size="small"
              />
            </Box>

            {project.description && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {project.description}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Progress Section */}
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" fontWeight="medium">
                  Development Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.tasksCompleted} / {project.tasksTotal} tasks
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={project.progress}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {project.progress}% complete
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Technologies */}
            <Box mb={2}>
              <Typography variant="body2" fontWeight="medium" mb={1}>
                Tech Stack
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {project.technologies.map((tech) => (
                  <Chip
                    key={tech}
                    label={tech}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Timeline */}
            {(project.startDate || project.endDate) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ScheduleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(project.startDate)} → {formatDate(project.endDate)}
                  </Typography>
                </Box>
              </>
            )}

            {/* GitHub Integration */}
            {project.githubRepo && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    GitHub Repository
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<GitHubIcon />}
                        endIcon={<LaunchIcon />}
                        onClick={() => handleGitHubClick(project)}
                      >
                        {project.githubRepo}
                      </Button>
                      {project.githubBranch && (
                        <Chip
                          label={project.githubBranch}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Stack direction="row" spacing={2}>
                      {typeof project.openPRs === 'number' && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PRIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {project.openPRs} open PR{project.openPRs !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                      {typeof project.openIssues === 'number' && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <IssueIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {project.openIssues} open issue{project.openIssues !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                      {project.lastCommit && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CodeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Last commit: {project.lastCommit}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}

            {/* Actions */}
            <Divider sx={{ my: 2 }} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={() => onViewSpec && onViewSpec(project.id)}
              >
                View Specification
              </Button>
              {project.githubRepo && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PRIcon />}
                    onClick={() => handleGitHubClick(project)}
                  >
                    View Pull Requests
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<IssueIcon />}
                    onClick={() => handleGitHubClick(project)}
                  >
                    View Issues
                  </Button>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default AssignedProjects;
